import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gql';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useContributionsPageState } from '#src/app/pages/accountSettings/contributions/main/pageContext';
import { CANCELLATION_SOURCE_ACCOUNT_SETTINGS, cancellationSourceToTrackingValue } from '#src/app/shared/constants';
import { usePerimeterXTracking } from '#src/app/shared/hooks/payments';
import { usePerimeterXCaptcha } from '#src/app/shared/hooks/perimeterX';
import { useRefreshSession } from '#src/app/shared/hooks/session';

import { cancelSubscriptionApi } from './api';
import type { CancelSubscriptionResult } from './api';

export function useCancelSubscription(): (options?: { onBeforeCaptcha?: () => void }) => Promise<void> {
	const [{ subscription }, { setPageMessage }] = useContributionsPageState();
	const track = useTracking();
	const utilityContext = useUtilityContext();
	const { translate } = useI18n();
	const navigate = useNavigate();
	const refreshSession = useRefreshSession();
	const triggerCaptcha = usePerimeterXCaptcha();
	const { trackShow, trackSuccess, trackFailure } = usePerimeterXTracking('cancel_subscription_contributions');

	// oxlint-disable-next-line max-statements -- exceeds the 18-statement limit; flagged as documented debt rather than refactored, per team decision
	return async function cancelSubscription(options?: { onBeforeCaptcha?: () => void }) {
		void track('subs_cancel_modal_confirm_click');

		let response: CancelSubscriptionResult;
		try {
			response = await cancelSubscriptionApi({ subscriptionId: subscription.id }, utilityContext);
		} catch (e) {
			if (!(e instanceof GQLResponsePerimeterXChallengeError)) return;
			options?.onBeforeCaptcha?.();
			trackShow(e.operationName);
			const solved = await triggerCaptcha({ pxResponse: e.pxChallenge });
			if (!solved) {
				trackFailure(e.pxChallenge, e.operationName);
				return;
			}
			trackSuccess(e.pxChallenge, e.operationName);
			response = await cancelSubscriptionApi({ subscriptionId: subscription.id }, utilityContext);
		}

		if (!response.success) {
			void track('subs_customer_cancellation_error', {
				cancellation_source: cancellationSourceToTrackingValue(CANCELLATION_SOURCE_ACCOUNT_SETTINGS),
			});
			setPageMessage({
				message: translate('fe.errors.generic_try_again'),
				severity: 'critical',
			});
		} else {
			void track('subs_customer_cancellation', {
				cancellation_source: cancellationSourceToTrackingValue(CANCELLATION_SOURCE_ACCOUNT_SETTINGS),
			});
			void refreshSession();
			navigate('/account_settings/contributions/cancel_confirmation');
		}
	};
}
